import * as RadixToast from '@radix-ui/react-toast'
import { create } from 'zustand'
import { cn } from '@/lib/utils'
import type { DsTone } from '@/ds/tone'
import { DsIconGlyph } from '@/ds/icon'
import { DsIcons } from '@/ds/icons'

export interface DsToastOptions {
  tone?: DsTone
  actionLabel?: string
  onAction?: () => void
  /** ms; default 4000 */
  duration?: number
}

interface ToastEntry extends DsToastOptions {
  id: number
  message: string
}

interface ToastState {
  current: ToastEntry | null
  show: (message: string, options?: DsToastOptions) => void
  dismiss: () => void
}

let toastId = 1
const useToastStore = create<ToastState>((set) => ({
  current: null,
  show: (message, options) => set({ current: { id: toastId++, message, ...options } }),
  dismiss: () => set({ current: null }),
}))

export const DsToast = {
  show: (message: string, options?: DsToastOptions) => useToastStore.getState().show(message, options),
  success: (message: string) => useToastStore.getState().show(message, { tone: 'success' }),
  error: (message: string, options?: Pick<DsToastOptions, 'actionLabel' | 'onAction'>) => useToastStore.getState().show(message, { tone: 'danger', ...options }),
  dismiss: () => useToastStore.getState().dismiss(),
}

const toneIcon: Partial<Record<DsTone, typeof DsIcons.success>> = { success: DsIcons.success, danger: DsIcons.error, warning: DsIcons.warning, info: DsIcons.info }

export function ToastHost() {
  const current = useToastStore((s) => s.current)
  const dismiss = useToastStore((s) => s.dismiss)
  return (
    <RadixToast.Provider swipeDirection="down" duration={current?.duration ?? 4000}>
      {current && (
        <RadixToast.Root
          key={current.id}
          open
          onOpenChange={(open) => !open && dismiss()}
          className="flex items-center gap-3 rounded-lg bg-foreground px-4 py-3 text-background shadow-md animate-ds-slide-up data-[state=closed]:animate-ds-fade-out"
        >
          {current.tone && toneIcon[current.tone] && <DsIconGlyph icon={toneIcon[current.tone]!} size={18} className={cn(current.tone === 'success' && 'text-success-vivid', current.tone === 'danger' && 'text-danger-vivid', current.tone === 'warning' && 'text-warning-vivid', current.tone === 'info' && 'text-info-vivid')} />}
          <RadixToast.Description className="flex-1 text-small-strong">{current.message}</RadixToast.Description>
          {current.actionLabel && (
            <RadixToast.Action altText={current.actionLabel} asChild>
              <button type="button" onClick={current.onAction} className="text-small-strong text-brand-vivid underline-offset-4 hover:underline">
                {current.actionLabel}
              </button>
            </RadixToast.Action>
          )}
        </RadixToast.Root>
      )}
      <RadixToast.Viewport className="fixed inset-x-4 bottom-[max(16px,env(safe-area-inset-bottom))] z-[60] flex flex-col items-center md:inset-x-auto md:right-6 md:bottom-6 md:w-[380px]" />
    </RadixToast.Provider>
  )
}
